import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { seasonsApi, Season, SeasonRegistration } from '../../api/seasons';
import { useTranslation } from 'react-i18next';
import { TrashButton } from '../../components/IconButton';

function toDateInput(iso?: string) {
  if (!iso) return '';
  return iso.slice(0, 10);
}

function EditModal({ season, onClose, onSaved }: {
  season: Season;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [err, setErr] = useState('');
  const [form, setForm] = useState({
    name: season.name,
    season_class: season.season_class,
    season_type: season.season_type,
    start_date: toDateInput(season.start_date),
    end_date: toDateInput(season.end_date),
    round_duration_days: season.round_duration_days,
    num_rounds: season.num_rounds,
    fee: season.fee ?? '',
    max_participants: season.max_participants ?? '',
    elo_min: season.elo_min ?? '',
    elo_max: season.elo_max ?? '',
    region: season.region ?? '',
    description: season.description ?? '',
  });

  const mut = useMutation({
    mutationFn: () => seasonsApi.update(season.id, {
      ...form,
      fee: form.fee !== '' ? Number(form.fee) : null,
      max_participants: form.max_participants !== '' ? Number(form.max_participants) : null,
      elo_min: form.elo_min !== '' ? Number(form.elo_min) : null,
      elo_max: form.elo_max !== '' ? Number(form.elo_max) : null,
    }),
    onSuccess: () => { onSaved(); onClose(); },
    onError: (e: any) => setErr(e.response?.data?.detail ?? 'Fehler beim Speichern'),
  });

  function set(k: string, v: unknown) { setForm(f => ({ ...f, [k]: v })); }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-xl shadow-xl overflow-y-auto max-h-[90vh]">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('season.editTitle')}</h3>

          {err && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm mb-4">{err}</div>}

          <div className="space-y-3">
            <div>
              <label className="label">{t('season.name')} *</label>
              <input className="input" value={form.name} onChange={e => set('name', e.target.value)} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">{t('season.class')}</label>
                <select className="input" value={form.season_class} onChange={e => set('season_class', e.target.value)}>
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                </select>
              </div>
              <div>
                <label className="label">{t('season.type')}</label>
                <select className="input" value={form.season_type} onChange={e => set('season_type', e.target.value)}>
                  <option value="singles">{t('season.typeSingles')}</option>
                  <option value="pairs">{t('season.typePairs')}</option>
                  <option value="wettkampf">{t('season.typeWettkampf')}</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">{t('season.startDate')}</label>
                <input type="date" className="input" value={form.start_date} onChange={e => set('start_date', e.target.value)} />
              </div>
              <div>
                <label className="label">{t('season.endDate')}</label>
                <input type="date" className="input" value={form.end_date} onChange={e => set('end_date', e.target.value)} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">{t('season.numRounds')}</label>
                <input type="number" min={1} className="input" value={form.num_rounds}
                  onChange={e => set('num_rounds', parseInt(e.target.value))} />
              </div>
              <div>
                <label className="label">{t('season.roundDays')}</label>
                <input type="number" min={7} className="input" value={form.round_duration_days}
                  onChange={e => set('round_duration_days', parseInt(e.target.value))} />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3">
              <div>
                <label className="label">{t('season.fee')}</label>
                <input type="number" step="0.50" className="input" value={form.fee}
                  onChange={e => set('fee', e.target.value)} placeholder="0,00" />
              </div>
              <div>
                <label className="label">{t('season.maxParticipants')}</label>
                <input type="number" className="input" value={form.max_participants}
                  onChange={e => set('max_participants', e.target.value)} placeholder="–" />
              </div>
              <div>
                <label className="label">ELO min</label>
                <input type="number" className="input" value={form.elo_min}
                  onChange={e => set('elo_min', e.target.value)} placeholder="–" />
              </div>
              <div>
                <label className="label">ELO max</label>
                <input type="number" className="input" value={form.elo_max}
                  onChange={e => set('elo_max', e.target.value)} placeholder="–" />
              </div>
            </div>

            <div>
              <label className="label">{t('season.region')}</label>
              <input className="input" value={form.region} onChange={e => set('region', e.target.value)} />
            </div>

            <div>
              <label className="label">{t('season.description')}</label>
              <textarea className="input" rows={3} value={form.description}
                onChange={e => set('description', e.target.value)} />
            </div>
          </div>

          <div className="flex gap-3 mt-5">
            <button onClick={() => mut.mutate()} disabled={!form.name || mut.isPending}
              className="btn btn-primary btn-sm">
              {mut.isPending ? '...' : t('courts.save')}
            </button>
            <button onClick={onClose} className="btn btn-secondary btn-sm">{t('admin.cancel')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminSeasonManage() {
  const { id } = useParams<{ id: string }>();
  const seasonId = parseInt(id!);
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [showEdit, setShowEdit] = useState(false);
  const [walkoverMatch, setWalkoverMatch] = useState<number | null>(null);
  const [walkoverUserId, setWalkoverUserId] = useState('');
  const [resultMatch, setResultMatch] = useState<number | null>(null);
  const [adminHomeSets, setAdminHomeSets] = useState('');
  const [adminAwaySets, setAdminAwaySets] = useState('');

  const { data: season, isLoading } = useQuery({
    queryKey: ['season', seasonId],
    queryFn: () => seasonsApi.get(seasonId).then(r => r.data),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['season', seasonId] });

  const activateMut = useMutation({
    mutationFn: () => seasonsApi.activate(seasonId),
    onSuccess: (r) => { invalidate(); setMsg((r.data as any).message); setErr(''); },
    onError: (e: any) => setErr(e.response?.data?.detail ?? 'Fehler'),
  });

  const finishMut = useMutation({
    mutationFn: () => seasonsApi.update(seasonId, { status: 'finished' }),
    onSuccess: () => { invalidate(); setMsg('Saison abgeschlossen'); setErr(''); },
    onError: (e: any) => setErr(e.response?.data?.detail ?? 'Fehler'),
  });

  const deleteMut = useMutation({
    mutationFn: () => seasonsApi.delete(seasonId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['seasons'] });
      navigate('/admin');
    },
    onError: (e: any) => setErr(e.response?.data?.detail ?? 'Löschen fehlgeschlagen'),
  });

  const archiveMut = useMutation({
    mutationFn: () => seasonsApi.archive(seasonId),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['seasons'] });
      setMsg('Saison archiviert');
      setErr('');
    },
    onError: (e: any) => setErr(e.response?.data?.detail ?? 'Archivieren fehlgeschlagen'),
  });

  const paymentMut = useMutation({
    mutationFn: ({ regId, status }: { regId: number; status: string }) =>
      seasonsApi.setPayment(seasonId, regId, status),
    onSuccess: () => { invalidate(); setMsg('Zahlungsstatus aktualisiert'); setErr(''); },
    onError: (e: any) => setErr(e.response?.data?.detail ?? 'Fehler'),
  });

  const walkoverMut = useMutation({
    mutationFn: (against: string | number) => seasonsApi.walkover(walkoverMatch!, against),
    onSuccess: () => { invalidate(); setWalkoverMatch(null); setWalkoverUserId(''); setMsg('Walkover gesetzt'); },
    onError: (e: any) => setErr(e.response?.data?.detail ?? 'Fehler'),
  });

  const closeRoundMut = useMutation({
    mutationFn: () => seasonsApi.closeRound(seasonId),
    onSuccess: (r) => { invalidate(); setMsg((r.data as any).message ?? 'Runde abgeschlossen'); setErr(''); },
    onError: (e: any) => setErr(e.response?.data?.detail ?? 'Fehler'),
  });

  const adminResultMut = useMutation({
    mutationFn: ({ matchId, home, away }: { matchId: number; home: number; away: number }) =>
      seasonsApi.adminResult(matchId, home, away),
    onSuccess: () => {
      invalidate();
      setResultMatch(null); setAdminHomeSets(''); setAdminAwaySets('');
      setMsg('Ergebnis eingetragen'); setErr('');
    },
    onError: (e: any) => setErr(e.response?.data?.detail ?? 'Fehler'),
  });

  if (isLoading || !season) return <div className="text-center py-16">{t('admin.loading')}</div>;

  const allMatches = season.rounds?.flatMap(r => r.matches) ?? [];
  const openMatches = allMatches.filter(m => !['played','walkover','bye'].includes(m.status));
  const byeMatches = allMatches.filter(m => m.status === 'bye');
  const activeRound = season.rounds?.find(r => r.status === 'active');
  const activeRoundOpenMatches = activeRound
    ? activeRound.matches.filter(m => !['played','walkover','bye'].includes(m.status))
    : [];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {showEdit && (
        <EditModal
          season={season}
          onClose={() => setShowEdit(false)}
          onSaved={() => { invalidate(); qc.invalidateQueries({ queryKey: ['seasons'] }); setMsg('Saison gespeichert'); }}
        />
      )}

      {walkoverMatch && (() => {
        const m = allMatches.find(x => x.id === walkoverMatch);
        if (!m) return null;
        const isPairs = !!m.home_pair;
        const isWettkampf = !!m.home_team;
        const isBye = isPairs ? !m.away_pair : isWettkampf ? !m.away_team : !m.away_user;

        type Option = { label: string; value: string };
        let options: Option[] = [];
        if (!isBye) {
          if (isWettkampf) {
            options = [
              m.home_team ? { label: m.home_team.name, value: String(m.home_team.id) } : null,
              m.away_team ? { label: m.away_team.name, value: String(m.away_team.id) } : null,
            ].filter(Boolean) as Option[];
          } else if (isPairs) {
            const hp = m.home_pair;
            const ap = m.away_pair;
            options = [
              hp ? { label: `${hp.player1?.first_name ?? '?'} / ${hp.player2?.first_name ?? '?'}`, value: String(hp.id) } : null,
              ap ? { label: `${ap.player1?.first_name ?? '?'} / ${ap.player2?.first_name ?? '?'}`, value: String(ap.id) } : null,
            ].filter(Boolean) as Option[];
          } else {
            options = [
              m.home_user ? { label: `${m.home_user.first_name} ${m.home_user.last_name}`, value: m.home_user.id } : null,
              m.away_user ? { label: `${m.away_user.first_name} ${m.away_user.last_name}`, value: m.away_user.id } : null,
            ].filter(Boolean) as Option[];
          }
        }

        const canConfirm = isBye || !!walkoverUserId;
        const getAgainst = (): string | number => {
          if (isBye) return '';
          return (isPairs || isWettkampf) ? parseInt(walkoverUserId) : walkoverUserId;
        };

        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
              <h3 className="font-semibold text-gray-900 mb-4">Walkover setzen</h3>
              {isBye ? (
                <p className="text-sm text-gray-600 mb-4">BYE-Match – die Heimseite gewinnt automatisch ohne ELO-Änderung.</p>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-4">{t('season.walkoverHint')}</p>
                  <div className="space-y-2 mb-4">
                    {options.map(opt => (
                      <label key={opt.value} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${walkoverUserId === opt.value ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}>
                        <input type="radio" name="walkover" value={opt.value} checked={walkoverUserId === opt.value}
                          onChange={e => setWalkoverUserId(e.target.value)} />
                        <span className="font-medium text-gray-900">{opt.label}</span>
                        <span className="text-xs text-gray-400">{t('season.walkoverAgainst')}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => walkoverMut.mutate(getAgainst())}
                  disabled={!canConfirm || walkoverMut.isPending}
                  className="btn btn-sm bg-red-600 hover:bg-red-700 text-white border-red-600">
                  Walkover bestätigen
                </button>
                <button onClick={() => { setWalkoverMatch(null); setWalkoverUserId(''); }} className="btn btn-secondary btn-sm">{t('admin.cancel')}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Admin result modal */}
      {resultMatch && (() => {
        const m = allMatches.find(x => x.id === resultMatch);
        if (!m) return null;
        const homeLabel = m.home_team?.name
          ?? (m.home_pair ? `${m.home_pair.player1?.first_name ?? '?'} / ${m.home_pair.player2?.first_name ?? '?'}` : `${m.home_user?.first_name ?? '?'} ${m.home_user?.last_name ?? ''}`);
        const awayLabel = m.away_team?.name
          ?? (m.away_pair ? `${m.away_pair.player1?.first_name ?? '?'} / ${m.away_pair.player2?.first_name ?? '?'}` : `${m.away_user?.first_name ?? '?'} ${m.away_user?.last_name ?? ''}`);
        const canSubmit = adminHomeSets !== '' && adminAwaySets !== '' && adminHomeSets !== adminAwaySets;
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
              <h3 className="font-semibold text-gray-900 mb-4">Ergebnis eintragen (Admin)</h3>
              <p className="text-sm text-gray-600 mb-4">{homeLabel} vs. {awayLabel}</p>
              <div className="flex items-center gap-3 mb-4">
                <div>
                  <label className="label text-xs">{homeLabel}</label>
                  <input type="number" min={0} max={9} className="input w-16 text-center"
                    value={adminHomeSets} onChange={e => setAdminHomeSets(e.target.value)} />
                </div>
                <span className="text-gray-400 font-bold mt-4">:</span>
                <div>
                  <label className="label text-xs">{awayLabel}</label>
                  <input type="number" min={0} max={9} className="input w-16 text-center"
                    value={adminAwaySets} onChange={e => setAdminAwaySets(e.target.value)} />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => adminResultMut.mutate({ matchId: resultMatch, home: parseInt(adminHomeSets), away: parseInt(adminAwaySets) })}
                  disabled={!canSubmit || adminResultMut.isPending}
                  className="btn btn-primary btn-sm">
                  {adminResultMut.isPending ? '...' : 'Ergebnis speichern'}
                </button>
                <button onClick={() => { setResultMatch(null); setAdminHomeSets(''); setAdminAwaySets(''); }} className="btn btn-secondary btn-sm">{t('admin.cancel')}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Header */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Link to="/admin" className="text-primary-600 hover:text-primary-700 text-sm">{t('admin.backToAdmin')}</Link>
        <span className="text-gray-300">/</span>
        <h1 className="text-xl font-bold text-gray-900">{season.name}</h1>
        <span className={`badge border text-xs ${season.status === 'open' ? 'bg-green-100 text-green-700 border-green-300' : season.status === 'active' ? 'bg-blue-100 text-blue-700 border-blue-300' : season.status === 'archived' ? 'bg-purple-100 text-purple-700 border-purple-300' : 'bg-gray-100 text-gray-500 border-gray-300'}`}>
          {t(`season.status_${season.status}`)}
        </span>
      </div>

      {err && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-4">{err}</div>}
      {msg && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm mb-4">{msg}</div>}

      {/* Actions */}
      <div className="card mb-6">
        <h2 className="font-semibold text-gray-900 mb-4">{t('admin.actions')}</h2>
        <div className="flex flex-wrap gap-3 items-center">
          <Link to={`/seasons/${seasonId}`} className="btn btn-secondary btn-sm">
            → {t('season.publicView')}
          </Link>
          <button onClick={() => setShowEdit(true)} className="btn btn-secondary btn-sm">
            ✏️ {t('season.editTitle')}
          </button>
          {season.status === 'open' && (
            <button onClick={() => { if (window.confirm('Saison aktivieren und Runden generieren?')) activateMut.mutate(); }}
              disabled={activateMut.isPending} className="btn btn-primary btn-sm">
              {activateMut.isPending ? '...' : t('season.activate')}
            </button>
          )}
          {season.status === 'active' && activeRound && activeRoundOpenMatches.length > 0 && (
            <button
              onClick={() => {
                if (window.confirm(`Runde ${activeRound.round_number} jetzt abschließen? ${activeRoundOpenMatches.length} offene Match(es) werden als Walkover (0 ELO) gewertet.`))
                  closeRoundMut.mutate();
              }}
              disabled={closeRoundMut.isPending}
              className="btn btn-secondary btn-sm border-orange-300 text-orange-700 hover:bg-orange-50">
              {closeRoundMut.isPending ? '...' : `Runde ${activeRound.round_number} abschließen`}
            </button>
          )}
          {season.status === 'active' && (
            <button onClick={() => { if (window.confirm('Saison abschließen?')) finishMut.mutate(); }}
              disabled={finishMut.isPending}
              className="btn btn-secondary btn-sm border-amber-300 text-amber-700 hover:bg-amber-50">
              {t('season.finish')}
            </button>
          )}
          <div className="ml-auto flex gap-3 items-center">
            {season.status !== 'archived' && (
              <button
                onClick={() => {
                  if (window.confirm(`Saison "${season.name}" archivieren? Sie bleibt inkl. aller Ergebnisse und der ELO-Historie erhalten, wird aber aus der aktiven Übersicht ausgeblendet.`)) {
                    setErr('');
                    archiveMut.mutate();
                  }
                }}
                disabled={archiveMut.isPending}
                className="btn btn-secondary btn-sm border-purple-300 text-purple-700 hover:bg-purple-50">
                {archiveMut.isPending ? '...' : `📦 ${t('season.archive')}`}
              </button>
            )}
            <TrashButton
              onClick={() => {
                if (window.confirm(`Saison "${season.name}" wirklich endgültig löschen?\n\nDabei werden auch alle Ergebnisse und die ELO-Historie unwiderruflich gelöscht. Falls du die Daten behalten, die Saison aber aus der Übersicht ausblenden möchtest, nutze stattdessen "Archivieren".`)) {
                  setErr('');
                  deleteMut.mutate();
                }
              }}
              disabled={deleteMut.isPending}
              title="Saison löschen"
            />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: t('season.participants'), val: season.registrations?.length ?? 0, color: 'text-gray-900' },
          { label: t('season.roundsCount'), val: season.rounds?.length ?? 0, color: 'text-gray-900' },
          { label: t('season.played'), val: allMatches.filter(m => m.status === 'played').length, color: 'text-green-600' },
          { label: t('season.pending'), val: openMatches.length, color: 'text-yellow-600' },
        ].map(s => (
          <div key={s.label} className="card text-center p-4">
            <div className={`text-3xl font-bold ${s.color}`}>{s.val}</div>
            <div className="text-xs text-gray-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Payment overview */}
      {season.fee != null && season.fee > 0 && (() => {
        const regs = season.registrations ?? [];
        const pending = regs.filter(r => r.payment_status === 'pending');
        const confirmed = regs.filter(r => r.payment_status === 'confirmed');
        const waived = regs.filter(r => r.payment_status === 'waived');
        if (pending.length === 0) return null;
        return (
          <div className="card mb-6 border-orange-200 bg-orange-50">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="font-semibold text-orange-800">
                  {pending.length} Zahlung{pending.length !== 1 ? 'en' : ''} ausstehend
                </p>
                <p className="text-xs text-orange-700 mt-0.5">
                  {confirmed.length} bestätigt · {waived.length} erlassen · {pending.length} offen
                  {' '}· Startgeld: {season.fee} €
                </p>
              </div>
              <p className="text-sm text-orange-800 font-medium">
                Ausstehend: {pending.map(r =>
                  r.pair ? `${r.pair.player1?.first_name ?? '?'}/${r.pair.player2?.first_name ?? '?'}`
                  : r.team ? r.team.name
                  : `${r.user.first_name} ${r.user.last_name}`
                ).join(', ')}
              </p>
            </div>
          </div>
        );
      })()}

      {/* BYE matches – auto-resolve */}
      {byeMatches.length > 0 && (
        <div className="card mb-6 border-amber-200">
          <h2 className="font-semibold text-gray-900 mb-2">BYE-Matches</h2>
          <p className="text-xs text-gray-500 mb-3">Diese Matches haben keinen Gegner. Walkover setzen um die Runde fortzusetzen.</p>
          <div className="space-y-2">
            {byeMatches.map(m => {
              const homeLabel = m.home_team?.name
                ?? (m.home_pair ? `${m.home_pair.player1?.first_name ?? '?'} / ${m.home_pair.player2?.first_name ?? '?'}` : `${m.home_user?.first_name ?? '?'} ${m.home_user?.last_name ?? ''}`);
              return (
                <div key={m.id} className="flex items-center justify-between gap-2 py-2 border-b border-gray-100 last:border-0 text-sm">
                  <span className="text-gray-700">{homeLabel} <span className="text-gray-400">vs. BYE</span></span>
                  <button onClick={() => { setWalkoverMatch(m.id); setWalkoverUserId(''); }}
                    className="btn btn-secondary btn-sm text-xs border-amber-300 text-amber-700 hover:bg-amber-50">
                    BYE-Walkover
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Open matches – walkover */}
      {openMatches.length > 0 && (
        <div className="card mb-6">
          <h2 className="font-semibold text-gray-900 mb-4">{t('season.openMatches')}</h2>
          <div className="space-y-2">
            {openMatches.map(m => {
              const homeLabel = m.home_team?.name
                ?? (m.home_pair ? `${m.home_pair.player1?.first_name ?? '?'} / ${m.home_pair.player2?.first_name ?? '?'}` : `${m.home_user?.first_name ?? '?'} ${m.home_user?.last_name ?? ''}`);
              const awayLabel = m.away_team?.name
                ?? (m.away_pair ? `${m.away_pair.player1?.first_name ?? '?'} / ${m.away_pair.player2?.first_name ?? '?'}` : `${m.away_user?.first_name ?? '?'} ${m.away_user?.last_name ?? ''}`);
              return (
                <div key={m.id} className="flex items-center justify-between gap-2 py-2 border-b border-gray-100 last:border-0 text-sm flex-wrap">
                  <span className="text-gray-700 flex-1 min-w-0">
                    {homeLabel}
                    <span className="text-gray-400 mx-2">vs.</span>
                    {awayLabel}
                  </span>
                  <span className="text-xs text-gray-400">{m.status}</span>
                  <button onClick={() => { setResultMatch(m.id); setAdminHomeSets(''); setAdminAwaySets(''); }}
                    className="btn btn-secondary btn-sm text-xs border-blue-300 text-blue-700 hover:bg-blue-50">
                    Ergebnis
                  </button>
                  <button onClick={() => { setWalkoverMatch(m.id); setWalkoverUserId(''); }}
                    className="btn btn-secondary btn-sm text-xs border-red-300 text-red-600 hover:bg-red-50">
                    Walkover
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Participants + payment */}
      {(season.registrations ?? []).length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900">{t('season.participants')}</h2>
            {season.fee && (
              <span className="text-sm text-gray-500">{t('season.feeLabel')}: <strong>{season.fee} €</strong></span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-2 px-3 text-gray-500 font-medium border-r border-gray-200">#</th>
                  <th className="text-left py-2 px-3 text-gray-500 font-medium border-r border-gray-200">{t('ranking.player')}</th>
                  <th className="text-center py-2 px-3 text-gray-500 font-medium border-r border-gray-200">ELO</th>
                  <th className="text-center py-2 px-3 text-green-600 font-medium border-r border-gray-200">S</th>
                  <th className="text-center py-2 px-3 text-red-500 font-medium border-r border-gray-200">N</th>
                  {season.fee && (
                    <th className="text-center py-2 px-3 text-gray-500 font-medium">{t('admin.colPaymentStatus')}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const seenT = new Set<number>(); const seenP = new Set<number>();
                  return [...(season.registrations ?? [])].filter(reg => {
                    if (reg.team) { if (seenT.has(reg.team.id)) return false; seenT.add(reg.team.id); return true; }
                    if (reg.pair) { if (seenP.has(reg.pair.id)) return false; seenP.add(reg.pair.id); return true; }
                    return true;
                  }).sort((a,b) => b.wins - a.wins || a.losses - b.losses);
                })().map((reg: SeasonRegistration, i) => (
                  <tr key={reg.id} className="border-b border-gray-100 last:border-0">
                    <td className="py-2 px-3 text-gray-400 border-r border-gray-200">{i+1}</td>
                    <td className="py-2 px-3 font-medium text-gray-900 border-r border-gray-200">
                      {reg.team ? (
                        <div>
                          <span className="text-xs text-gray-400 font-normal">{reg.team.name} · </span>
                          <span className="font-semibold">{reg.user.first_name} {reg.user.last_name}</span>
                        </div>
                      ) : reg.pair ? (
                        <>
                          {reg.pair.player1?.first_name} {reg.pair.player1?.last_name}
                          <span className="text-gray-300 text-xs ml-1">({reg.pair.player1?.elo})</span>
                          <span className="text-gray-400 mx-1">/</span>
                          {reg.pair.player2?.first_name} {reg.pair.player2?.last_name}
                          <span className="text-gray-300 text-xs ml-1">({reg.pair.player2?.elo})</span>
                        </>
                      ) : (
                        <>{reg.user.first_name} {reg.user.last_name}</>
                      )}
                    </td>
                    <td className="py-2 px-3 text-center text-gray-700 border-r border-gray-200">
                      {reg.pair
                        ? `⌀ ${reg.pair.current_elo ?? Math.round(((reg.pair.player1?.elo ?? 0) + (reg.pair.player2?.elo ?? 0)) / 2)}`
                        : reg.team
                          ? `⌀ ${reg.elo_at_registration}`
                          : reg.elo_at_registration}
                    </td>
                    <td className="py-2 px-3 text-center font-bold text-green-600 border-r border-gray-200">{reg.wins}</td>
                    <td className="py-2 px-3 text-center font-bold text-red-500 border-r border-gray-200">{reg.losses}</td>
                    {season.fee && (
                      <td className="py-2 px-3 text-center">
                        <select
                          value={reg.payment_status}
                          onChange={e => paymentMut.mutate({ regId: reg.id, status: e.target.value })}
                          disabled={paymentMut.isPending}
                          className={`text-xs border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-400 ${
                            reg.payment_status === 'confirmed' ? 'bg-green-50 text-green-700 border-green-300' :
                            reg.payment_status === 'waived'    ? 'bg-blue-50 text-blue-700 border-blue-300' :
                            'bg-yellow-50 text-yellow-700 border-yellow-300'
                          }`}
                        >
                          <option value="pending">{t('admin.paymentPending')}</option>
                          <option value="confirmed">{t('admin.paymentConfirmed')}</option>
                          <option value="waived">{t('admin.paymentWaived')}</option>
                        </select>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
